/**
 * Airport Detection Module
 *
 * Pure utility functions for detecting whether booking pickup/dropoff
 * coordinates fall within the radius of configured airports.
 * Uses the Haversine formula for great-circle distance calculation.
 */

/** Earth's radius in miles */
const EARTH_RADIUS_MILES = 3959

/** Configured airport with detection radius */
export interface AccountAirport {
  readonly id: string
  readonly name: string
  readonly lat: number
  readonly lng: number
  readonly radiusMiles: number
}

/** Result of matching a single coordinate against airports */
export interface AirportMatch {
  readonly id: string
  readonly name: string
  readonly distanceMiles: number
}

/** Result of detecting airports for a complete trip (pickup + dropoff) */
export interface AirportDetectionResult {
  readonly isAirportPickup: boolean
  readonly isAirportDropoff: boolean
  readonly matchedPickupAirport?: AirportMatch
  readonly matchedDropoffAirport?: AirportMatch
}

/** Coordinate pair */
interface Coords {
  readonly lat: number
  readonly lng: number
}

/** Convert degrees to radians */
function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180
}

/**
 * Calculate the great-circle distance between two points using the Haversine formula.
 * Returns distance in miles.
 */
export function haversineDistanceMiles(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const dLat = toRadians(lat2 - lat1)
  const dLng = toRadians(lng2 - lng1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return EARTH_RADIUS_MILES * c
}

/**
 * Check if coordinates fall within any airport's detection radius.
 * Returns the closest matching airport (smallest distance) if found.
 */
export function detectAirportByRadius(
  coords: Coords,
  airports: ReadonlyArray<AccountAirport>
): { readonly matched: boolean; readonly airport?: AccountAirport; readonly distanceMiles?: number } {
  let closestAirport: AccountAirport | undefined
  let closestDistance = Infinity

  for (const airport of airports) {
    const distance = haversineDistanceMiles(
      coords.lat,
      coords.lng,
      airport.lat,
      airport.lng
    )

    if (distance <= airport.radiusMiles && distance < closestDistance) {
      closestAirport = airport
      closestDistance = distance
    }
  }

  if (closestAirport) {
    return {
      matched: true,
      airport: closestAirport,
      distanceMiles: Math.round(closestDistance * 100) / 100,
    }
  }

  return { matched: false }
}

/**
 * Detect airports for both pickup and dropoff coordinates independently.
 * Null coordinates result in no detection for that leg.
 */
export function detectTripAirports(
  pickupCoords: Coords | null,
  dropoffCoords: Coords | null,
  airports: ReadonlyArray<AccountAirport>
): AirportDetectionResult {
  const pickupResult = pickupCoords
    ? detectAirportByRadius(pickupCoords, airports)
    : { matched: false }

  const dropoffResult = dropoffCoords
    ? detectAirportByRadius(dropoffCoords, airports)
    : { matched: false }

  return {
    isAirportPickup: pickupResult.matched,
    isAirportDropoff: dropoffResult.matched,
    ...(pickupResult.matched && pickupResult.airport
      ? {
          matchedPickupAirport: {
            id: pickupResult.airport.id,
            name: pickupResult.airport.name,
            distanceMiles: pickupResult.distanceMiles!,
          },
        }
      : {}),
    ...(dropoffResult.matched && dropoffResult.airport
      ? {
          matchedDropoffAirport: {
            id: dropoffResult.airport.id,
            name: dropoffResult.airport.name,
            distanceMiles: dropoffResult.distanceMiles!,
          },
        }
      : {}),
  }
}
